import { useCallback, useEffect, useRef, useState } from 'react';
import Lenis from 'lenis';
import './App.css';
import Analytics from './Analytics';
import { useLang } from './i18n';
import logotypeClear from './assets/logotype-clear.svg';
import logomarkClear from './assets/logomark-clear.svg';
import typemark from './assets/typemark.svg';
import iconMark from './assets/icon.svg';

// Teaser mode: only hero + footer show by default. Reveal everything at runtime
// with the keyboard shortcut Ctrl/Cmd + Shift + M (toggles; persisted).
const REVEAL_KEY = 'mynstur-reveal-all';

const prefersReduced = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* Reveal-on-scroll, progressively enhanced: content is visible without JS, and
   the offset/animation only applies once the `.js` flag is on <html>. */
function useReveal() {
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('js');
    const els = document.querySelectorAll('[data-reveal]');

    if (prefersReduced() || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

/* The brand made literal: a streaming "signal readout". Each horizontal
   scanline is a 1-bit dither of dashes whose density follows a top-dense
   vertical gradient — solid bars up top thinning to sparse ticks below, like
   a feed of impressions. The pattern drifts slowly sideways. Density is a
   deterministic hash (no per-frame flicker); it pauses off-screen and renders
   a single static frame under prefers-reduced-motion. */
function DitherField({ color = '255, 80, 45' }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduce = prefersReduced();
    const LINE_GAP = 6; // vertical distance between scanlines
    const LINE_THICK = 2.5; // scanline thickness (gap is LINE_GAP - thickness)
    const CELL = 4; // horizontal dash quantization
    const colorStr = `rgb(${color})`;
    let w = 0;
    let h = 0;
    let cols = 0;
    let lines = 0;
    let raf = 0;
    let running = false;
    let last = 0;
    let offset = 0; // horizontal drift in px

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      w = Math.max(1, rect.width);
      h = Math.max(1, rect.height);
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cols = Math.ceil(w / CELL) + 2;
      lines = Math.ceil(h / LINE_GAP) + 1;
    }

    // Deterministic 0..1 hash — stable per (cell, line) so dashes don't flicker.
    function hash(x, y) {
      let n = (x * 374761393 + y * 668265263) | 0;
      n = (n ^ (n >> 13)) | 0;
      n = Math.imul(n, 1274126177) | 0;
      return ((n ^ (n >> 16)) >>> 0) / 4294967295;
    }

    function density(ny) {
      const d = 1.06 * Math.pow(1 - ny, 1.35); // dense at top, fades down
      return Math.max(0, Math.min(0.9, d));
    }

    function render() {
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = colorStr;
      const phase = Math.floor(offset / CELL);
      const sub = offset - phase * CELL;
      for (let row = 0; row < lines; row++) {
        const y = row * LINE_GAP;
        const base = density(y / h);
        if (base < 0.004) continue;
        let runStart = -1;
        for (let s = 0; s <= cols; s++) {
          const ai = s + phase;
          // gentle low-frequency horizontal swell so dashes clump organically
          const swell = 0.78 + 0.32 * Math.sin(ai * 0.03 + row * 0.22);
          const on = s < cols && hash(ai, row) < base * swell;
          const x = s * CELL - sub;
          if (on && runStart < 0) {
            runStart = x;
          } else if (!on && runStart >= 0) {
            ctx.fillRect(runStart, y, x - runStart, LINE_THICK);
            runStart = -1;
          }
        }
      }
    }

    function loop(now) {
      raf = requestAnimationFrame(loop);
      if (now - last < 33) return; // throttle ~30fps
      last = now;
      offset += 0.35; // slow leftward stream
      render();
    }
    function start() {
      if (running || reduce) return;
      running = true;
      last = 0;
      raf = requestAnimationFrame(loop);
    }
    function stop() {
      running = false;
      cancelAnimationFrame(raf);
    }

    resize();
    render();

    // Only animate while the canvas is actually on screen.
    let io;
    if (!reduce && 'IntersectionObserver' in window) {
      io = new IntersectionObserver(
        (entries) => entries.forEach((e) => (e.isIntersecting ? start() : stop())),
        { threshold: 0 }
      );
      io.observe(canvas);
    } else if (!reduce) {
      start();
    }

    const onResize = () => {
      resize();
      render();
    };
    window.addEventListener('resize', onResize);
    return () => {
      stop();
      if (io) io.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [color]);

  return <canvas className="signal" ref={ref} aria-hidden="true" />;
}

export default function App() {
  const { t, lang, setLang } = useLang();
  useReveal();
  const lenisRef = useRef(null);
  const heroInnerRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleLang = () => setLang(lang === 'is' ? 'en' : 'is');

  // CTA lead form: POST to the Pages Function, then fire a Clero conversion.
  const [lead, setLead] = useState({ status: 'idle' });
  const submitLead = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const email = form.elements.email.value.trim();
    const company = form.elements.company.value; // honeypot, stays empty
    setLead({ status: 'sending' });
    try {
      const res = await fetch('/api/lead', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, lang, company }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body.ok) throw new Error(body.error || String(res.status));
      setLead({ status: 'sent' });
      form.reset();
      // Conversion event — only meaningful once consent is granted; the Clero
      // pixel drops 'event' calls made before that, so we gate it here too.
      try {
        if (
          localStorage.getItem('mynstur-cookie-consent') === 'granted' &&
          typeof window.clero === 'function'
        ) {
          window.clero('event', 'lead', { form: 'cta', lang });
        }
      } catch {
        /* ignore */
      }
    } catch {
      setLead({ status: 'error' });
    }
  };

  // Feature flag: reveal all hidden sections. Toggle with Ctrl/Cmd + Shift + M
  // (persisted in localStorage so it survives reloads).
  const [showSections, setShowSections] = useState(() => {
    try {
      return localStorage.getItem(REVEAL_KEY) === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'm') {
        e.preventDefault();
        setShowSections((v) => {
          const next = !v;
          try {
            localStorage.setItem(REVEAL_KEY, next ? '1' : '0');
          } catch {
            /* ignore */
          }
          return next;
        });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /* Lenis smooth scrolling (skipped under reduced-motion → native scroll). */
  useEffect(() => {
    if (prefersReduced()) return undefined;
    const lenis = new Lenis({
      duration: 1.1,
      easing: (x) => (x === 1 ? 1 : 1 - Math.pow(2, -10 * x)), // ease-out-expo
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 1.5,
    });
    lenisRef.current = lenis;
    let raf = 0;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  /* One rAF-throttled scroll handler: nav state + hero parallax-out. */
  useEffect(() => {
    const reduce = prefersReduced();
    const inner = heroInnerRef.current;
    let raf = 0;
    const apply = () => {
      const y = window.scrollY;
      setScrolled(y > 24);
      if (!reduce && inner) {
        const p = Math.min(1, y / (window.innerHeight * 0.9));
        inner.style.transform = `translate3d(0, ${y * 0.16}px, 0)`;
        inner.style.opacity = String(Math.max(0, 1 - p * 1.05));
      }
      raf = 0;
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(apply);
    };
    apply();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  /* Lock scroll while the overlay menu is open; Esc closes it. */
  useEffect(() => {
    const root = document.documentElement;
    if (menuOpen) {
      root.classList.add('menu-open');
      lenisRef.current?.stop();
    } else {
      root.classList.remove('menu-open');
      lenisRef.current?.start();
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const go = useCallback((e, hash) => {
    e.preventDefault();
    setMenuOpen(false);
    const target = document.querySelector(hash);
    if (!target) return;
    const lenis = lenisRef.current;
    if (lenis) {
      lenis.start();
      lenis.scrollTo(target, { offset: 0, duration: 1.2 });
    } else {
      target.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  return (
    <div className="app">
      <Analytics />
      {/* NAV */}
      <header className={`nav ${scrolled ? 'nav--scrolled' : ''} ${menuOpen ? 'nav--menu' : ''}`}>
        <div className="nav__blur" aria-hidden="true">
          <div /><div /><div /><div /><div /><div />
        </div>
        <div className="nav__scrim" aria-hidden="true" />
        <a className="nav__brand" href="#top" onClick={(e) => go(e, '#top')} aria-label="Mynstur home">
          <img src={logotypeClear} alt="Mynstur" />
        </a>
        <div className="nav__right">
          {showSections && (
            <>
              <button className="nav__lang" onClick={toggleLang} aria-label={t.switchLabel} title={t.switchLabel}>
                {t.switchTo}
              </button>
              <a className="nav__cta" href="#contact" onClick={(e) => go(e, '#contact')}>
                {t.nav.cta}
              </a>
              <button
                className={`nav__toggle ${menuOpen ? 'is-open' : ''}`}
                onClick={() => setMenuOpen((v) => !v)}
                aria-expanded={menuOpen}
                aria-controls="overlay-menu"
              >
                <span className="nav__toggle-label">{menuOpen ? t.nav.close : t.nav.menu}</span>
                <span className="burger" aria-hidden="true"><span /><span /></span>
              </button>
            </>
          )}
        </div>
      </header>

      {/* FULL-SCREEN OVERLAY MENU */}
      <div
        id="overlay-menu"
        className={`menu ${menuOpen ? 'is-open' : ''}`}
        aria-hidden={!menuOpen}
      >
        <div className="menu__field" aria-hidden="true">
          {menuOpen && (
            <video className="menu__video" autoPlay muted loop playsInline aria-hidden="true">
              <source src={`${process.env.PUBLIC_URL}/footer-video.mp4`} type="video/mp4" />
            </video>
          )}
        </div>
        {showSections && (
          <nav className="menu__nav" aria-label="Primary">
            {t.menu.items.map((n, i) => (
              <a
                key={n.href}
                className="menu__link"
                style={{ '--i': i }}
                href={n.href}
                onClick={(e) => go(e, n.href)}
                tabIndex={menuOpen ? 0 : -1}
              >
                <span className="menu__link-inner">{n.label}</span>
                <span className="menu__link-idx">{`0${i + 1}`}</span>
              </a>
            ))}
          </nav>
        )}
        <div className="menu__foot">
          <div className="menu__block">
            <span className="menu__cap">{t.menu.getInTouch}</span>
            <a className="menu__contact" href={t.footer.emailHref}>
              {t.footer.email}
            </a>
            <a className="menu__contact" href={t.footer.phoneHref}>{t.footer.phone}</a>
          </div>
          <div className="menu__block">
            <span className="menu__cap">{t.menu.follow}</span>
            <div className="menu__socials">
              {t.menu.socials.map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" tabIndex={menuOpen ? 0 : -1}>
                  {s.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* HERO */}
      <section className="hero" id="top">
        <video
          className="hero__video"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
        >
          <source src={`${process.env.PUBLIC_URL}/hero-dither.webm`} type="video/webm" />
        </video>
        <div className="hero__veil" aria-hidden="true" />
        <div className="hero__grid" ref={heroInnerRef}>
          <div className="hero__head">
            <h1 className="hero__title">
              {t.hero.title.a}<br /><em>{t.hero.title.em}</em>
            </h1>
            {showSections && (
              <div className="hero__actions">
                <a className="btn btn--brand" href="#contact" onClick={(e) => go(e, '#contact')}>
                  {t.hero.ctaPrimary} <span className="btn__arrow" aria-hidden="true">→</span>
                </a>
                <a className="btn btn--line-light" href="#platform" onClick={(e) => go(e, '#platform')}>
                  {t.hero.ctaSecondary}
                </a>
              </div>
            )}
          </div>
          {showSections && <p className="hero__sub hero__lead">{t.hero.sub}</p>}
        </div>
      </section>

      {showSections && (
        <>
      {/* RESULTS — signal bar */}
      <section className="metrics" id="results" aria-label={t.menu.items[2].label}>
        <p className="metrics__intro" data-reveal>{t.metrics.intro}</p>
        <div className="metrics__row">
          {t.metrics.items.map((s, i) => (
            <div
              className={`metric ${s.lead ? 'metric--lead' : ''}`}
              key={i}
              data-reveal
              style={{ '--d': `${i * 70}ms` }}
            >
              <div className="metric__value">{s.value}</div>
              <div className="metric__label">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="services" id="services">
        <div className="services__head" data-reveal>
          <h2 className="services__title">{t.services.title}</h2>
          <p className="services__lead">{t.services.lead}</p>
          <a className="link-arrow" href="#contact" onClick={(e) => go(e, '#contact')}>
            {t.services.link} <span aria-hidden="true">→</span>
          </a>
        </div>
        <ul className="services__list">
          {t.services.items.map((s) => (
            <li className="srow" key={s.title} data-reveal>
              <h3 className="srow__title">{s.title}</h3>
              <p className="srow__body">{s.body}</p>
              <div className="srow__tags">
                {s.tags.map((tag) => (
                  <span key={tag}>{tag}</span>
                ))}
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* PLATFORM BANNER */}
      <section className="platform" id="platform">
        <img
          className="platform__bg"
          src={`${process.env.PUBLIC_URL}/platform-bg.jpg`}
          alt=""
          aria-hidden="true"
        />
        <div className="platform__veil" aria-hidden="true" />
        <div className="platform__inner" data-reveal>
          <img className="platform__mark" src={logomarkClear} alt="" aria-hidden="true" />
          <h2 className="platform__title">
            {t.platform.title.l1}<br />{t.platform.title.l2}<em>{t.platform.title.em}</em>
          </h2>
          <p className="platform__body">{t.platform.body}</p>
          <a className="btn btn--brand" href="#contact" onClick={(e) => go(e, '#contact')}>
            {t.platform.cta} <span className="btn__arrow" aria-hidden="true">→</span>
          </a>
        </div>
      </section>

      {/* APPROACH — a real three-step sequence */}
      <section className="approach" id="approach">
        <div className="approach__head" data-reveal>
          <h2 className="approach__title">{t.approach.title}</h2>
        </div>
        <ol className="approach__steps">
          {t.approach.items.map((a, i) => (
            <li className="step" key={i} data-reveal style={{ '--d': `${i * 90}ms` }}>
              <div className="step__num">{`0${i + 1}`}</div>
              <div className="step__name">{a.step}</div>
              <h3 className="step__title">{a.title}</h3>
              <p className="step__body">{a.body}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* CTA */}
      <section className="cta" id="contact">
        <DitherField color="26, 24, 20" />
        <div className="cta__inner" data-reveal>
          <h2 className="cta__title">
            {t.ctaSection.title.pre}<em>{t.ctaSection.title.em}</em>{t.ctaSection.title.post}
          </h2>
          <p className="cta__sub">{t.ctaSection.sub}</p>
          <form className="cta__form" onSubmit={submitLead}>
            <input
              type="email"
              name="email"
              placeholder={t.ctaSection.placeholder}
              aria-label="Your work email"
              required
              disabled={lead.status === 'sending'}
            />
            {/* Honeypot — hidden from people, filled only by bots. */}
            <input
              type="text"
              name="company"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
            />
            <button className="btn btn--dark" type="submit" disabled={lead.status === 'sending'}>
              {lead.status === 'sending' ? t.ctaSection.sending : t.ctaSection.button}{' '}
              <span className="btn__arrow" aria-hidden="true">→</span>
            </button>
          </form>
          <p className="cta__fine" role="status" aria-live="polite">
            {lead.status === 'sent'
              ? t.ctaSection.success
              : lead.status === 'error'
                ? t.ctaSection.error
                : t.ctaSection.fine}
          </p>
        </div>
      </section>
        </>
      )}

      {/* FOOTER — editorial */}
      <footer className="footer">
        <div className="footer__head" data-reveal>
          {showSections && <p className="footer__intro">{t.footer.intro}</p>}
          <p className="footer__addr">
            {t.footer.addr.map((line, i) => (
              <span key={i}>
                {line}
                {i < t.footer.addr.length - 1 && <br />}
              </span>
            ))}
          </p>
          <div className="footer__reach">
            <a href={t.footer.emailHref}>{t.footer.email}</a>
            <a href={t.footer.email2Href}>{t.footer.email2}</a>
            <a href={t.footer.phoneHref}>{t.footer.phone}</a>
          </div>
        </div>

        <div className="footer__band" aria-hidden="true">
          <video className="footer__band-video" autoPlay muted loop playsInline preload="auto" aria-hidden="true">
            <source src={`${process.env.PUBLIC_URL}/footer-video.mp4`} type="video/mp4" />
          </video>
        </div>

        <div className="footer__wordmark" aria-hidden="true">
          <img className="footer__icon" src={iconMark} alt="" />
          <img className="footer__type" src={typemark} alt="Mynstur" />
        </div>
      </footer>
    </div>
  );
}
